import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, Camera, X, Flashlight } from 'lucide-angular';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

// BarcodeDetector Web API uchun tip
interface BarcodeDetectorResult {
  rawValue: string;
  format: string;
}
declare class BarcodeDetector {
  constructor(options?: { formats: string[] });
  detect(source: HTMLVideoElement | HTMLImageElement): Promise<BarcodeDetectorResult[]>;
}

@Component({
  selector: 'app-barcode-scanner',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, TranslatePipe],
  templateUrl: './barcode-scanner.component.html',
})
export class BarcodeScannerComponent implements OnDestroy {
  private readonly translate = inject(TranslateService);

  @Output() barcodeDetected = new EventEmitter<string>();
  @ViewChild('videoEl') videoEl!: ElementRef<HTMLVideoElement>;

  /**
   * Komponentning o'z tugmasi ko'rsatilsinmi.
   *
   * `false` bo'lganda skanerni tashqaridan `startScan()` bilan ishga tushirish
   * kerak — chaqiruvchida allaqachon tugma bo'lsa, ikkita tugma ketma-ket
   * bosilishiga yo'l qo'ymaslik uchun.
   */
  @Input() showTrigger = true;

  Camera = Camera;
  X = X;
  Flashlight = Flashlight;

  isScanning = signal(false);
  isSupported = signal(true);
  errorMsg = signal<string | null>(null);

  private stream: MediaStream | null = null;
  private animFrame: number | null = null;
  private detector: BarcodeDetector | null = null;
  private errorTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Xatoni suzuvchi banner sifatida ko'rsatadi va o'zi o'chiradi.
   *
   * Xabar tugma ostida emas — chaqiruvchi tugmani yashirgan bo'lishi mumkin, va
   * oqim ichidagi matn sarlavha qatorini surib yuborardi.
   */
  private showError(message: string): void {
    this.errorMsg.set(message);
    if (this.errorTimer) clearTimeout(this.errorTimer);
    this.errorTimer = setTimeout(() => this.errorMsg.set(null), 5000);
  }

  get canUseDetector(): boolean {
    return 'BarcodeDetector' in window;
  }

  async startScan() {
    this.errorMsg.set(null);

    if (!this.canUseDetector) {
      this.isSupported.set(false);
      this.showError(this.translate.instant('BARCODE_SCANNER.NOT_SUPPORTED'));
      return;
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
      });

      this.isScanning.set(true);

      // Video element render bo'lishini kutamiz
      requestAnimationFrame(() => {
        if (this.videoEl?.nativeElement) {
          this.videoEl.nativeElement.srcObject = this.stream;
          this.videoEl.nativeElement.play().then(() => this.runDetection());
        }
      });
    } catch {
      this.showError(this.translate.instant('BARCODE_SCANNER.CAMERA_DENIED'));
    }
  }

  private runDetection() {
    if (!this.detector) {
      this.detector = new BarcodeDetector({
        formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'code_93', 'upc_a', 'upc_e', 'qr_code'],
      });
    }

    const detect = async () => {
      if (!this.isScanning() || !this.videoEl?.nativeElement) return;
      const video = this.videoEl.nativeElement;
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        try {
          const results = await this.detector!.detect(video);
          if (results.length > 0) {
            this.barcodeDetected.emit(results[0].rawValue);
            this.stopScan();
            return;
          }
        } catch {
          // Frame o'qish xatosi — keyingi frameni sinab ko'ramiz
        }
      }
      this.animFrame = requestAnimationFrame(detect);
    };

    this.animFrame = requestAnimationFrame(detect);
  }

  stopScan() {
    if (this.animFrame !== null) {
      cancelAnimationFrame(this.animFrame);
      this.animFrame = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    this.isScanning.set(false);
  }

  ngOnDestroy() {
    this.stopScan();
    if (this.errorTimer) clearTimeout(this.errorTimer);
  }
}
